import { releaseCanvas } from '@/tools/image-engine';
import { decodeImage } from '@/tools/images/use-image-session';
import { createPdfEngine, type PagePlan, type PdfEngine } from '@/tools/pdf-engine';

/** The server's cap on the raw PDF (design §2.4). */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/** One page of the document being built from photos: a JPEG, after any straightening. */
export interface PageImage {
  readonly id: string;
  readonly name: string;
  readonly dataUrl: string;
  readonly width: number;
  readonly height: number;
}

let pageCounter = 0;

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/** JPEG, PNG, WebP, and HEIC/HEIF (which the browser may report with no type at all). */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name);
}

/**
 * A photo as a page: decoded through a canvas and written back as JPEG, so a HEIC from an iPhone
 * leaves as a JPEG like any other (design §2.4). Rejects when the browser cannot decode it.
 */
export async function imageFileToPage(file: File): Promise<PageImage> {
  const canvas = await decodeImage(file);
  try {
    return pageFromCanvas(canvas, file.name);
  } finally {
    releaseCanvas(canvas);
  }
}

export function pageFromCanvas(canvas: HTMLCanvasElement, name: string): PageImage {
  pageCounter += 1;
  return {
    id: `page-${pageCounter}`,
    name,
    dataUrl: canvas.toDataURL('image/jpeg', 0.92),
    width: canvas.width,
    height: canvas.height,
  };
}

/** Reads a JPEG data URL's natural size. */
export async function pageFromDataUrl(dataUrl: string, name: string): Promise<PageImage> {
  const image = new Image();
  image.src = dataUrl;
  await image.decode();
  pageCounter += 1;
  return {
    id: `page-${pageCounter}`,
    name,
    dataUrl,
    width: image.naturalWidth,
    height: image.naturalHeight,
  };
}

/** A4 portrait in PDF points. */
export const A4 = { width: 595.28, height: 841.89 } as const;
const MARGIN = 18;

/** The box an image of `width`×`height` takes on an A4 page: fitted inside the margins, centred. */
export function fitOnA4(width: number, height: number) {
  const maxW = A4.width - 2 * MARGIN;
  const maxH = A4.height - 2 * MARGIN;
  const scale = Math.min(maxW / width, maxH / height);
  const w = width * scale;
  const h = height * scale;
  return { x: (A4.width - w) / 2, y: (A4.height - h) / 2, width: w, height: h };
}

/** A one-page blank A4 PDF, written by hand so the engine has a page to draw the photos on. */
export function blankA4Pdf(): Blob {
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4.width} ${A4.height}] /Resources << >> >>`,
  ];
  let body = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((object, index) => {
    offsets.push(body.length);
    body += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xref = body.length;
  body += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) body += `${String(offset).padStart(10, '0')} 00000 n \n`;
  body += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new Blob([body], { type: 'application/pdf' });
}

function dataUrlBytes(dataUrl: string): ArrayBuffer {
  const binary = atob(dataUrl.split(',')[1] ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/**
 * «Crea il PDF»: the pages, in order, one per A4 page, with the PDF tool's engine (in a Worker
 * where the browser allows it).
 */
export async function buildA4Pdf(pages: readonly PageImage[], engine?: PdfEngine): Promise<Blob> {
  const pdf = engine ?? createPdfEngine();
  try {
    const blank = await pdf.open(blankA4Pdf(), 'a4.pdf');
    const plan: PagePlan[] = pages.map((page) => ({
      source: blank.id,
      page: 0,
      images: [
        {
          bytes: dataUrlBytes(page.dataUrl),
          type: page.dataUrl.startsWith('data:image/png') ? 'png' : 'jpeg',
          ...fitOnA4(page.width, page.height),
        },
      ],
    }));
    const out = await pdf.assemble(plan);
    pdf.close(blank.id);
    return out;
  } finally {
    if (engine === undefined) pdf.dispose();
  }
}

/** Opens a chosen PDF to learn its pages; rejects when it cannot be read (or needs a password). */
export async function readPdfPages(file: Blob, engine?: PdfEngine): Promise<number> {
  const pdf = engine ?? createPdfEngine();
  try {
    const opened = await pdf.open(file);
    pdf.close(opened.id);
    return opened.info.pages;
  } finally {
    if (engine === undefined) pdf.dispose();
  }
}
